import type { IPermission, IScopedEntity } from '@mb/auth/shared';
import { PERMISSION_ACTION } from '@mb/auth/shared';

/** Flat string gates (admin-style, no entity/action/scope decomposition). */
export const ORGANIZATION_PERMISSION = {
  ORGANIZATION_ADMIN: 'access.permission.organization.admin',
} as const;

export const organizationPermissionCatalog: IPermission[] = [
  {
    value: ORGANIZATION_PERMISSION.ORGANIZATION_ADMIN,
    group: 'access.permission.group.organization',
    description: 'access.permission.organization.admin.description',
  },
];

/**
 * Catalog keys for scopable entities. Must match `rolePermissionTable.entity`
 * and the keys used by `buildScopeFilter`.
 */
export const ORGANIZATION_ENTITY = {
  PERSON: 'person',
  POSITION: 'position',
  PERSON_POSITION: 'personPosition',
  // The org-chart is governed by `organigram` (read filters visible nodes;
  // create/update/delete gate node edits). It replaces the former
  // `organizationUnit` scoped entity, which governed nothing else.
  ORGANIGRAM: 'organigram',
} as const;

const ALL_ACTIONS = [
  PERMISSION_ACTION.CREATE,
  PERMISSION_ACTION.READ,
  PERMISSION_ACTION.UPDATE,
  PERMISSION_ACTION.DELETE,
] as const;

/**
 * Custom (non-CRUD) actions. Each is a free-text key persisted in
 * `rolePermissionTable.action` and shown as its own scoped row in the matrix.
 * Add a `permission.action.{key}` i18n entry for the row label.
 */
export const PERSON_ACTION = {
  SEND_INVITE: 'sendInvite',
} as const;

/**
 * Scopable entities for the permission matrix (rows = entity × action,
 * columns = scope). Aggregated into ScopedPermissionManager at startup.
 *
 * An entity's `actions` may mix bare CRUD verbs with custom action descriptors
 * `{ action, label?, scopes? }` — e.g. `person` also exposes `sendInvite`.
 */
export const organizationScopedEntities: IScopedEntity[] = [
  {
    entity: ORGANIZATION_ENTITY.PERSON,
    label: 'organization.entity.person',
    group: 'organization.entity.group.people',
    actions: [
      ...ALL_ACTIONS,
      {
        action: PERSON_ACTION.SEND_INVITE,
        label: 'permission.action.sendInvite',
      },
    ],
  },
  {
    entity: ORGANIZATION_ENTITY.POSITION,
    label: 'organization.entity.position',
    group: 'organization.entity.group.structure',
    actions: ALL_ACTIONS,
  },
  {
    entity: ORGANIZATION_ENTITY.PERSON_POSITION,
    label: 'organization.entity.personPosition',
    group: 'organization.entity.group.structure',
    actions: ALL_ACTIONS,
  },
  {
    entity: ORGANIZATION_ENTITY.ORGANIGRAM,
    label: 'organization.entity.organigram',
    group: 'organization.entity.group.structure',
    actions: ALL_ACTIONS,
  },
];
